// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useMemo } from 'react';
import { LineChart } from '@carbon/charts-react';
import { ScaleTypes } from '@carbon/charts';
import type { LineChartOptions } from '@carbon/charts';
import { useEquipmentTelemetry } from '../../hooks/useEquipmentTelemetry';
import { useShellStore } from '../../stores/shellStore';
import type { MetricSeries } from '../../types';

interface AlarmTelemetryProps {
  siteId?: string;
  equipmentId?: string;
  orgId?: string;
}

function MetricChart({ series, theme }: { series: MetricSeries; theme: string }) {
  // Carbon's TIME scale wants a Date; it then auto-thins ticks to fit the axis
  // width instead of drawing one label per point (which overlapped badly).
  const data = useMemo(
    () => series.data.map(d => ({ group: d.group, date: new Date(d.key), value: d.value })),
    [series.data]
  );

  const options = useMemo<LineChartOptions>(
    () => ({
      // Title is rendered as our own element below — Carbon's built-in title sits
      // in a header element that doesn't pick up the dark card background.
      axes: {
        bottom: { mapsTo: 'date', scaleType: ScaleTypes.TIME },
        left: {
          mapsTo: 'value',
          title: series.unit || undefined,
          scaleType: ScaleTypes.LINEAR,
        },
      },
      timeScale: {
        // Show hour-of-day ticks (e.g. "14:00") across the 24h window.
        timeIntervalFormats: {
          '15minutes': { primary: 'HH:mm', secondary: 'HH:mm' },
          '30minutes': { primary: 'HH:mm', secondary: 'HH:mm' },
          hourly: { primary: 'HH:mm', secondary: 'HH:mm' },
        },
      },
      curve: 'curveMonotoneX',
      points: { enabled: false },
      legend: { enabled: false },
      toolbar: { enabled: false },
      height: '240px',
      theme,
    }),
    [series.unit, theme]
  );

  return (
    <div className="ad-telemetry-chart">
      <div className="ad-telemetry-metric-title">
        {series.name}
        {series.unit ? <span className="ad-telemetry-metric-unit"> ({series.unit})</span> : null}
      </div>
      <LineChart data={data} options={options} />
    </div>
  );
}

/**
 * Past-24h equipment telemetry, rendered as one Carbon line chart per metric.
 * Renders nothing while loading and nothing when there is no telemetry to show
 * (missing ids, empty result, or a failed fetch) — telemetry is a graceful
 * enhancement of the alarm detail page, never a blocker.
 */
export default function AlarmTelemetry({ siteId, equipmentId, orgId }: AlarmTelemetryProps) {
  const { series, loading } = useEquipmentTelemetry(siteId, equipmentId, orgId);
  const theme = useShellStore(s => s.theme);

  if (loading || series.length === 0) return null;

  return (
    <section className="ad-card">
      <div className="ad-card-title">Telemetry · past 24h</div>
      <div className="ad-telemetry-charts">
        {series.map(s => (
          <MetricChart key={s.name} series={s} theme={theme} />
        ))}
      </div>
    </section>
  );
}
